import React from 'react'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { DownloadFileData, DownloadFileItem, DownloadsParent, UpdateFileFormValues } from '../../models'
import useModals from 'src/Hooks/useModals'
import { deleteDownloadFile, updateFile } from '../../api'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useDispatch } from 'react-redux'
import { refreshApiView } from '@e3dc-react/shell/State/ApiView/ApiViewActions'
import { useUploadFileFormInitializer } from '../../hooks'
import DownloadItemForm from './DownloadItemForm'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { DownloadItemAccess } from '../../dto'

export interface DownloadItemEditorProps {
  item: DownloadFileItem
  parent?: DownloadsParent
  onClose: () => void
  selectCategory: (categoryId?: number) => void
}

const DownloadItemEditor: React.FC<DownloadItemEditorProps> = ({ item, parent, onClose, selectCategory }) => {
  const { t } = useCustomTranslation()
  const authFetch = useAuthFetchWithResponse()
  const { openMoveDownloadItemDialog, openConfirmationModal, openReplaceFileDialog } = useModals()
  const { enqueueErrorStack, enqueueSuccessStack } = useSnackbar()
  const { initialValues, validationSchema } = useUploadFileFormInitializer({
    parent,
    item,
    isPublic: Boolean(item.access === DownloadItemAccess.PUBLIC),
  })
  const dispatch = useDispatch()
  const isEditing = !!item
  const methods = useForm<UpdateFileFormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(validationSchema),
    mode: 'onChange',
  })

  const onDeleteFile = (onCloseConfirmation: () => void): void => {
    if (item) {
      deleteDownloadFile(item.id, authFetch)
        .then(() => {
          onCloseConfirmation()
          onClose()
          dispatch(refreshApiView())
          enqueueSuccessStack(t('documents.delete-file.success'))
        })
        .catch(error => {
          console.error('Error deleting file:', error)
          enqueueErrorStack(t('documents.delete-file.error'))
        })
    }
  }

  const onSubmit = (values: UpdateFileFormValues): void => {
    const { isPublic, parentId, fileName, ...payloadValues } = values

    const payload: Omit<DownloadFileData, 'path'> = {
      access: values.isPublic ? DownloadItemAccess.PUBLIC : DownloadItemAccess.PORTAL,
      ...payloadValues,
      urlName: payloadValues.urlName || null,
    }

    if (fileName !== item?.fileName) {
      payload.fileName = fileName
    }

    updateFile(item.id, payload, authFetch)
      .then(() => {
        enqueueSuccessStack(t('documents.edit-file.success'))
        onClose()
      })
      .catch(error => {
        enqueueErrorStack(t('documents.edit-file.error'))
        console.error('Error updating file:', error)
      })
      .finally(() => dispatch(refreshApiView()))
  }

  return (
    <FormProvider {...methods}>
      <Dialog
        title={isEditing ? t('documents.edit-item.title') : t('documents.add-file.title')}
        maxWidth="xs"
        onClose={onClose}
        actions={{
          primary: {
            name: isEditing ? t('common.save') : t('common.create'),
            onClick: methods.handleSubmit(onSubmit),
            disabled: !methods.formState.isValid,
          },
          secondary: [
            {
              name: t('common.cancel'),
              onClick: onClose,
            },
          ],
        }}
        stackActions={
          item
            ? [
                {
                  name: t('documents.edit-file.actions.move'),
                  onClick: () => {
                    onClose()
                    openMoveDownloadItemDialog({ itemToMoveId: item.id, selectCategory, isFile: true })
                  },
                },
                {
                  name: t('documents.edit-file.actions.replace'),
                  onClick: () => {
                    onClose()
                    openReplaceFileDialog({ item })
                  },
                },
                {
                  name: t('documents.edit-file.actions.delete'),
                  onClick: () => {
                    openConfirmationModal({
                      title: t('documents.delete-file.confirmationTitle'),
                      text: t('documents.delete-file.confirmationText', { name: item.fileName }),
                      onConfirm: onClose => onDeleteFile(onClose),
                      buttonText: [t('common.cancel'), t('common.delete')],
                    })
                  },
                },
              ]
            : undefined
        }
      >
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <DownloadItemForm parent={parent} />
        </form>
      </Dialog>
    </FormProvider>
  )
}

export default DownloadItemEditor
